import { jsxs, jsx } from "react/jsx-runtime";
import * as React from "react";
import { forwardRef } from "react";
import styled, { css } from "styled-components";
import { semanticColors, colors, cbSemanticCssVars, typographyCss } from "@creditornot/cb-ingredients";
import { createCssVars, createCssVarDefinitions, createThemedCssVarDefinitions, configureCssVars } from "@creditornot/cb-utils";
const colorsConfig = {
  borderUnchecked: {
    light: semanticColors.light.borderLight,
    lightHighContrast: semanticColors.lightHighContrast.borderLight,
    dark: colors.salt32
  },
  borderChecked: {
    light: colors.wolt100,
    lightHighContrast: colors.darkSky100,
    dark: colors.wolt100
  },
  borderCheckedDisabled: {
    light: colors.pepper20,
    lightHighContrast: colors.pepper40,
    dark: colors.salt32
  },
  bgUnchecked: {
    light: semanticColors.light.surfaceMain,
    lightHighContrast: semanticColors.light.surfaceMain,
    dark: semanticColors.dark.surfaceMain
  },
  bgChecked: {
    light: semanticColors.light.surfaceMain,
    lightHighContrast: semanticColors.light.surfaceMain,
    dark: semanticColors.dark.surfaceMain
  },
  bgDisabled: {
    light: colors.pepper12,
    lightHighContrast: colors.pepper12,
    dark: colors.salt16
  },
  bgActive: {
    light: colors.pepper8,
    lightHighContrast: colors.pepper8,
    dark: colors.salt12
  },
  icon: {
    light: colors.wolt100,
    lightHighContrast: colors.darkSky100,
    dark: colors.wolt100
  },
  iconDisabled: {
    light: colors.pepper32,
    lightHighContrast: colors.pepper60,
    dark: colors.salt32
  }
};
const cssVars = createCssVars(["size", "borderWidth"]);
const colorCssVars$2 = createCssVars([
  "borderUnchecked",
  "borderChecked",
  "borderCheckedDisabled",
  "bgUnchecked",
  "bgChecked",
  "bgDisabled",
  "bgActive",
  "icon",
  "iconDisabled"
]);
const Root$2 = styled.div`
  ${createCssVarDefinitions({
  size: "16px",
  borderWidth: "1px"
})}

  ${createThemedCssVarDefinitions(colorsConfig)}

  flex-shrink: 0;
  position: relative;
  z-index: 0;
  border-radius: 50%;
  display: block;

  width: ${cssVars.size};
  height: ${cssVars.size};

  @keyframes zoomIn {
    0% {
      transform: scale(0.4);
    }

    75% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes zoomOut {
    0% {
      transform: scale(1);
    }
    100% {
      transform: scale(0.4);
    }
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @keyframes fadeOut {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;
const BG = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  border-radius: 50%;
  border-style: solid;
  border-width: ${cssVars.borderWidth};
  border-color: ${colorCssVars$2.borderUnchecked};
  transition:
    background-color 100ms ease-in,
    border-color 100ms ease-in,
    opacity 100ms ease-in,
    border-color 100ms ease-in;
`;
const CheckedFill = styled.div`
  background-color: ${colorCssVars$2.icon};
  width: 50%;
  height: 50%;
  border-radius: 50%;
  animation: zoomOut 300ms;
`;
const IconContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  transition: opacity 50ms ease-in;
  opacity: 0;
`;
const Input = styled.input`
  margin: 0;
  opacity: 0;
  outline: none;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  box-sizing: border-box;
  width: 100%;
  height: 100%;

  &:disabled {
    cursor: not-allowed;
  }

  &:disabled ~ ${IconContainer} > ${CheckedFill} {
    background-color: ${colorCssVars$2.iconDisabled};
  }

  &:not(:disabled):checked ~ ${BG} {
    background-color: ${colorCssVars$2.bgChecked};
    border-color: ${colorCssVars$2.borderChecked};
  }

  &:disabled ~ ${BG} {
    background-color: ${colorCssVars$2.bgDisabled};
  }

  &:disabled:checked ~ ${BG} {
    border-color: ${colorCssVars$2.borderCheckedDisabled};
  }

  &:checked ~ ${IconContainer} {
    opacity: 1;
  }

  &:checked ~ ${IconContainer} > ${CheckedFill} {
    animation: zoomIn 300ms;
  }

  ${({ $disableFocusAndActiveStyles }) => !$disableFocusAndActiveStyles && css`
      /* change background on click */
      &:not(:disabled):active ~ ${BG} {
        background-color: ${colorCssVars$2.bgActive};
      }

      :focus-visible ~ ${BG} {
        box-shadow: 0 0 0 2px ${cbSemanticCssVars.a11yFocusIndicator};
        outline: 2px transparent solid;
      }
    `}
`;
const InputRadio = ({
  checked,
  className,
  disabled,
  disableFocusAndActiveStyles = false,
  inputClassName,
  inputStyle,
  style,
  ...rest
}, ref) => /* @__PURE__ */ jsxs(Root$2, { className, style, children: [
  /* @__PURE__ */ jsx(
    Input,
    {
      $disableFocusAndActiveStyles: disableFocusAndActiveStyles,
      ...rest,
      checked,
      className: inputClassName,
      disabled,
      ref,
      style: inputStyle,
      type: "radio"
    }
  ),
  /* @__PURE__ */ jsx(BG, {}),
  /* @__PURE__ */ jsx(IconContainer, { children: /* @__PURE__ */ jsx(CheckedFill, {}) })
] });
const InputRadio$1 = forwardRef(InputRadio);
const { cssString: colorCssString, cssVars: colorCssVars$1 } = configureCssVars({
  label: {
    light: colors.pepper100,
    dark: colors.salt100
  },
  labelActive: {
    light: colors.pepper80,
    dark: colors.salt80
  }
});
const Root$1 = styled.div`
  ${colorCssString}

  display: inline-block;
  position: relative;
`;
const TextLabel = styled.span`
  ${typographyCss.Body3()}
  color: ${colorCssVars$1.label};
`;
const Label = styled.label`
  display: flex;
  align-items: center;

  &:active ${TextLabel} {
    color: ${colorCssVars$1.labelActive};
  }
`;
const Content$1 = styled.span`
  margin-inline-start: 8px;
`;
const Radio = React.forwardRef(
  ({
    checked,
    children,
    className,
    disabled,
    id,
    inputProps,
    name,
    onBlur,
    onChange,
    onClick,
    onFocus,
    readOnly,
    required,
    rootRef,
    style,
    value
  }, ref) => /* @__PURE__ */ jsx(Root$1, { className, ref: rootRef, style, children: /* @__PURE__ */ jsxs(Label, { htmlFor: id, children: [
    /* @__PURE__ */ jsx(
      InputRadio$1,
      {
        ...inputProps,
        checked,
        disabled,
        id,
        name,
        onBlur,
        onChange,
        onClick,
        onFocus,
        readOnly,
        ref,
        required,
        value
      }
    ),
    children !== void 0 && /* @__PURE__ */ jsx(Content$1, { children: typeof children === "string" ? /* @__PURE__ */ jsx(TextLabel, { children }) : children })
  ] }) })
);
Radio.displayName = "Radio";
const colorCssVars = createCssVars(["border", "borderChecked"]);
const Root = styled.label`
  ${createThemedCssVarDefinitions({
  border: {
    light: semanticColors.light.borderLight,
    lightHighContrast: colors.pepper48,
    dark: semanticColors.dark.borderLight
  },
  borderChecked: colors.wolt100
})}

  box-sizing: border-box;
  display: flex;
  position: relative;
  align-items: center;

  border-radius: 8px;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ $checked, $disabled }) => $checked && !$disabled ? colorCssVars.borderChecked : colorCssVars.border};
  background-color: transparent;
  padding: 12px;
  cursor: ${({ $disabled }) => $disabled ? "not-allowed" : "pointer"};

  transition:
    background-color 100ms ease-in,
    border-color 16ms ease-in;

  &:focus-within {
    outline: 2px transparent solid;
    box-shadow: 0 0 0 2px ${cbSemanticCssVars.a11yFocusIndicator};
  }
`;
const LeftCol = styled.div`
  flex: 1;
`;
const RightCol = styled.div`
  padding-inline-start: 12px;
`;
const Header = styled.div`
  display: flex;
  align-items: flex-start;
`;
const RadioContainer = styled.div`
  flex-shrink: 0;
  padding-top: 2px;
  padding-inline-end: 8px;
`;
const InputLabel = styled.span`
  ${typographyCss.Body3()}
  flex: 1;
`;
const Content = styled.div`
  padding-inline-start: 24px;
`;
const TextContent = styled.span`
  ${typographyCss.Small()}
  color: ${cbSemanticCssVars.textSecondary};
`;
const StyledInputRadio = styled(InputRadio$1)`
  &:focus {
    box-shadow: none;
  }
`;
const RadioCard = React.forwardRef(
  ({
    checked = false,
    className,
    disabled = false,
    inputProps,
    label,
    name,
    onBlur,
    onChange,
    onClick,
    onFocus,
    readOnly,
    required,
    rightContent,
    rootRef,
    style,
    subContent,
    title,
    value
  }, ref) => /* @__PURE__ */ jsxs(
    Root,
    {
      $checked: checked,
      $disabled: disabled,
      className,
      ref: rootRef,
      style,
      title,
      children: [
        /* @__PURE__ */ jsxs(LeftCol, { children: [
          /* @__PURE__ */ jsxs(Header, { children: [
            /* @__PURE__ */ jsx(RadioContainer, { children: /* @__PURE__ */ jsx(
              StyledInputRadio,
              {
                ...inputProps,
                checked,
                disableFocusAndActiveStyles: true,
                disabled,
                name,
                onBlur,
                onChange,
                onClick,
                onFocus,
                readOnly,
                ref,
                required,
                value
              }
            ) }),
            /* @__PURE__ */ jsx(InputLabel, { children: label })
          ] }),
          typeof subContent === "string" ? /* @__PURE__ */ jsx(Content, { children: /* @__PURE__ */ jsx(TextContent, { children: subContent }) }) : /* @__PURE__ */ jsx(Content, { children: subContent })
        ] }),
        !!rightContent && /* @__PURE__ */ jsx(RightCol, { children: rightContent })
      ]
    }
  )
);
RadioCard.displayName = "RadioCard";
export {
  InputRadio$1 as InputRadio,
  Radio,
  RadioCard
};
//# sourceMappingURL=index.js.map
